import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import FlightCard from './FlightCard';
import { fetchFlightClients } from './clientsRealtime';
import { fetchFlights } from './flightsRealtime';
import AirplaneSelector from './AirplaneSelector';
import './styles.css';

const useWakeUpRefetch = (refetchCallback) => {
  useEffect(() => {
    const handleWakeUp = (event) => {
      refetchCallback();
    };

    window.addEventListener('focus', handleWakeUp);
    window.addEventListener('pageshow', handleWakeUp);
    document.addEventListener('resume', handleWakeUp, false);

    return () => {
      window.removeEventListener('focus', handleWakeUp);
      window.removeEventListener('pageshow', handleWakeUp);
      document.removeEventListener('resume', handleWakeUp, false);
    };
  }, [refetchCallback]);
};

const FlightBoard = () => {
  const location = useLocation();
  const history = useHistory();
  const [flightClients, setFlightClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flights, setFlights] = useState([]);
  const [currentFlightIndex, setCurrentFlightIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 480);
  const [desktopFlightStart, setDesktopFlightStart] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState({ flights: false, clients: false });
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const subscriptionsRef = useRef({ flightClients: null, flights: null });
  const isInitialLoad = useRef(true);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const searchParams = new URLSearchParams(location.search);
  const locationIdParam = searchParams.get('location');
  const airplaneIdParam = searchParams.get('airplane');

  const refetchDataOnly = useCallback(async () => {
    try {
      await fetchFlightClients(setFlightClients, null, { locationId: locationIdParam, airplaneId: airplaneIdParam });
      await fetchFlights(setFlights, null, { locationId: locationIdParam, airplaneId: airplaneIdParam });
    } catch (error) {
      console.error('Data refetch failed:', error);
    }
  }, [locationIdParam, airplaneIdParam]);

  const fetchData = useCallback(async () => {
    const flightClientsResult = await fetchFlightClients(setFlightClients, null, { locationId: locationIdParam, airplaneId: airplaneIdParam });
    const flightsResult = await fetchFlights(setFlights, null, { locationId: locationIdParam, airplaneId: airplaneIdParam });

    if (subscriptionsRef.current.flightClients) {
      subscriptionsRef.current.flightClients.unsubscribe();
    }
    if (subscriptionsRef.current.flights) {
      subscriptionsRef.current.flights.unsubscribe();
    }

    if (flightClientsResult.subscription) {
      setConnectionStatus((prev) => ({ ...prev, clients: true }));

      flightClientsResult.subscription.on('open', () => {
        setConnectionStatus((prev) => ({ ...prev, clients: true }));

        if (!isInitialLoad.current) {
          refetchDataOnly();
        }
      });
      flightClientsResult.subscription.on('close', () => {
        setConnectionStatus((prev) => ({ ...prev, clients: false }));
      });
    } else {
      setConnectionStatus((prev) => ({ ...prev, clients: false }));
    }

    if (flightsResult.subscription) {
      setConnectionStatus((prev) => ({ ...prev, flights: true }));

      flightsResult.subscription.on('open', () => {
        setConnectionStatus((prev) => ({ ...prev, flights: true }));

        if (!isInitialLoad.current) {
          refetchDataOnly();
        }
      });
      flightsResult.subscription.on('close', () => {
        setConnectionStatus((prev) => ({ ...prev, flights: false }));
      });
    } else {
      setConnectionStatus((prev) => ({ ...prev, flights: false }));
    }

    subscriptionsRef.current = {
      flightClients: flightClientsResult.subscription,
      flights: flightsResult.subscription,
    };

    isInitialLoad.current = false;
  }, [refetchDataOnly, locationIdParam, airplaneIdParam]);

  useWakeUpRefetch(fetchData);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setShowSnackbar(false);
    };

    const handleOffline = () => {
      setIsOnline(false);
      if (!isInitialLoad.current) {
        setShowSnackbar(true);
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!isOnline && !isInitialLoad.current) {
      setShowSnackbar(true);
    } else if (isOnline) {
      setShowSnackbar(false);
    }
  }, [isOnline]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 480);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile && flights.length > 0) {
      const reversedFlights = [...flights].reverse();
      const inTheAirIndex = reversedFlights.findIndex((flight) => flight.status === 'in the air');
      const nextIndex = reversedFlights.findIndex((flight) => flight.status === 'next');

      let selectedIndex = 0;
      if (inTheAirIndex !== -1) {
        selectedIndex = inTheAirIndex;
      } else if (nextIndex !== -1) {
        selectedIndex = nextIndex;
      }

      setCurrentFlightIndex(selectedIndex);
    } else {
      setCurrentFlightIndex(0);
    }
  }, [flights, isMobile]);

  useEffect(() => {
    if (!isMobile && flights.length > 0) {
      const reversedFlights = [...flights].reverse();
      const inTheAirIndex = reversedFlights.findIndex((flight) => flight.status === 'in the air');
      const nextIndex = reversedFlights.findIndex((flight) => flight.status === 'next');
      const completedIndex = reversedFlights.findIndex((flight) => flight.status === 'completed');

      let startIndex = 0;
      if (inTheAirIndex !== -1) {
        startIndex = Math.max(0, inTheAirIndex - 2);
      } else if (nextIndex !== -1) {
        startIndex = Math.max(0, nextIndex - 2);
      } else if (completedIndex !== -1) {
        startIndex = Math.max(0, completedIndex - 2);
      }

      setDesktopFlightStart(startIndex);
    } else {
      setDesktopFlightStart(0);
    }
  }, [flights, isMobile]);

  useEffect(() => {
    if (!locationIdParam || !airplaneIdParam) return undefined;
    setLoading(true);
    fetchData().finally(() => setLoading(false));

    return () => {
      if (subscriptionsRef.current.flightClients) {
        subscriptionsRef.current.flightClients.unsubscribe();
      }
      if (subscriptionsRef.current.flights) {
        subscriptionsRef.current.flights.unsubscribe();
      }
    };
  }, [location.search, fetchData, locationIdParam, airplaneIdParam]);

  const handleAirplaneSelect = useCallback((airplaneId) => {
    const next = new URLSearchParams(location.search);
    next.set('airplane', airplaneId);
    history.replace({ pathname: location.pathname, search: `?${next.toString()}` });
  }, [history, location.pathname, location.search]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!isMobile || flights.length <= 1) return;

    const swipeThreshold = 50;
    const swipeDistance = touchStartX.current - touchEndX.current;

    if (Math.abs(swipeDistance) > swipeThreshold) {
      if (swipeDistance > 0) {
        setCurrentFlightIndex((prev) => (prev < flights.length - 1 ? prev + 1 : prev));
      } else {
        setCurrentFlightIndex((prev) => (prev > 0 ? prev - 1 : prev));
      }
    }
  };

  const handleDesktopPrev = () => {
    if (desktopFlightStart > 0) {
      setDesktopFlightStart(Math.max(0, desktopFlightStart - 3));
    }
  };

  const handleDesktopNext = () => {
    if (desktopFlightStart < reversedFlights.length - 3) {
      setDesktopFlightStart(Math.min(desktopFlightStart + 3, reversedFlights.length - 3));
    }
  };

  const renderSnackbar = () => {
    if (!showSnackbar) return null;
    return (
      <div className="snackbar">
        <div className="snackbar-content">
          <span className="snackbar-icon">⚠️</span>
          <span className="snackbar-text">Connection lost. Trying to reconnect...</span>
          <button className="snackbar-close" onClick={() => setShowSnackbar(false)}>
            ×
          </button>
        </div>
      </div>
    );
  };
  const locations = {
    1: 'Las Vegas ',
    2: 'Oceanside',
    5: 'Hawaii',
    7: 'New York',
  };
  const locationId = locationIdParam;
  const locationName = locations[locationId];

  const renderTopNav = () => (
    <div className="flights-topnav">
      <button
        className="back-button"
        aria-label="Go back"
        onClick={() => {
          if (history.length > 1) {
            history.goBack();
          } else {
            history.push('/');
          }
        }}>
        <svg
          className="back-icon"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>
      <div className="flights-topnav-title">{locationName} Flights</div>
      <div className="flights-topnav-right" />
    </div>
  );

  if (locationIdParam && !airplaneIdParam) {
    return (
      <>
        {renderTopNav()}
        {renderSnackbar()}
        <AirplaneSelector locationId={locationIdParam} onSelect={handleAirplaneSelect} />
      </>
    );
  }

  if (loading) {
    return (
      <>
        {renderTopNav()}
        {renderSnackbar()}
        <div className="loading-container">
          <div className="spinner"></div>
          <div className="loading-text">Loading flight data...</div>
        </div>
      </>
    );
  }

  const reversedFlights = [...flights].reverse();

  if (reversedFlights.length === 0) {
    return (
      <>
        {renderTopNav()}
        {renderSnackbar()}
        <div className="no-flights-container">
          <div className="no-flights-text">No available flights at the moment</div>
        </div>
      </>
    );
  }

  if (isMobile && reversedFlights.length > 0) {
    return (
      <>
        {renderTopNav()}
        {renderSnackbar()}
        <div className="flight-carousel-container">
          <div
            className="flight-carousel"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}>
            <div
              className="flight-carousel-track"
              style={{
                transform: `translateX(-${currentFlightIndex * 100}%)`,
                transition: 'transform 0.3s ease-in-out',
              }}>
              {reversedFlights.map((flight) => (
                <div key={flight.objectId} className="flight-carousel-slide">
                  <FlightCard flightId={flight.objectId} clients={flightClients} flight={flight} flights={flights} />
                </div>
              ))}
            </div>
          </div>

          {reversedFlights.length > 1 && (
            <div className="flight-carousel-indicators">
              {reversedFlights.map((_, index) => (
                <button
                  key={index}
                  className={`carousel-dot ${index === currentFlightIndex ? 'active' : ''}`}
                  onClick={() => setCurrentFlightIndex(index)}
                  aria-label={`Go to flight ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </>
    );
  }

  const visibleFlights = reversedFlights.slice(desktopFlightStart, desktopFlightStart + 3);

  return (
    <>
      {renderTopNav()}
      {renderSnackbar()}
      <div className="desktop-flights-container">
        <div className="flight-board-grid-1">
          {visibleFlights.map((flight) => (
            <FlightCard
              key={flight.objectId}
              flightId={flight.objectId}
              clients={flightClients}
              flight={flight}
              flights={flights}
            />
          ))}
        </div>
        {reversedFlights.length > 3 && (
          <div className="desktop-flights-nav">
            <button
              className={`nav-button prev ${desktopFlightStart === 0 ? 'disabled' : ''}`}
              onClick={handleDesktopPrev}
              disabled={desktopFlightStart === 0}
              aria-label="Previous flights">
              ‹
            </button>
            <div className="flight-counter">
              {desktopFlightStart + 1}-{Math.min(desktopFlightStart + 3, reversedFlights.length)} of{' '}
              {reversedFlights.length}
            </div>
            <button
              className={`nav-button next ${desktopFlightStart >= reversedFlights.length - 3 ? 'disabled' : ''}`}
              onClick={handleDesktopNext}
              disabled={desktopFlightStart >= reversedFlights.length - 3}
              aria-label="Next flights">
              ›
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default FlightBoard;
